/**
 * Bonding curve and post-graduation AMM. Pure bigint math, no floats.
 *
 * Phase 1 "curve": constant product on virtual reserves (pump.fun style).
 *   price = vZat / vTok. Real reserves track what was actually paid in and
 *   how many curve tokens are left. realZat === vZat - initialVirtualZat.
 * Phase 2 "amm": once every curve token is sold the pool graduates into a
 *   plain constant-product pool with the collected ZEC and the reserved
 *   token supply as real reserves.
 *
 * Every rounding goes in the pool's favour, so k never decreases and the pool
 * can always pay every seller.
 */
export const BPS = 10_000n;

export type PoolMode = 'curve' | 'amm';

export interface Pool {
  mode: PoolMode;
  vZat: bigint; // pricing reserve, ZEC side (zatoshi)
  vTok: bigint; // pricing reserve, token side (base units)
  realZat: bigint; // ZEC actually held for this pool
  realTok: bigint; // tokens actually held for this pool
  reserveTok: bigint; // tokens set aside for graduation liquidity
}

export interface CurveParams {
  totalSupply: bigint;
  curveSupply: bigint;
  virtualZat: bigint;
  virtualTokens: bigint;
}

export interface FeeParams {
  tradeBps: bigint;
  creatorBps: bigint;
  /** Extra fee that stays in the pool (raises the price for holders); the anti-snipe fee after launch. */
  poolBps?: bigint;
}

export interface BuyQuote {
  zatUsed: bigint; // gross ZEC taken from the buyer
  refund: bigint; // part of the input not used (only when the curve sells out)
  fee: bigint; // platform fee
  creatorFee: bigint;
  poolFee: bigint; // added to the pool after the trade, buys no tokens
  net: bigint; // ZEC that buys tokens
  tokensOut: bigint;
  graduates: boolean;
}

export interface SellQuote {
  tokensIn: bigint;
  gross: bigint; // ZEC the sale is worth at the curve
  fee: bigint;
  creatorFee: bigint;
  poolFee: bigint; // left in the pool
  zatOut: bigint; // ZEC credited to the seller
}

export function newPool(p: CurveParams): Pool {
  if (p.curveSupply <= 0n || p.curveSupply > p.totalSupply) throw new Error('bad curveSupply');
  if (p.virtualTokens <= p.curveSupply) throw new Error('virtualTokens must exceed curveSupply');
  if (p.virtualZat <= 0n) throw new Error('bad virtualZat');
  return {
    mode: 'curve',
    vZat: p.virtualZat,
    vTok: p.virtualTokens,
    realZat: 0n,
    realTok: p.curveSupply,
    reserveTok: p.totalSupply - p.curveSupply,
  };
}

const ceilDiv = (a: bigint, b: bigint): bigint => {
  if (a < 0n || b <= 0n) throw new Error('ceilDiv domain');
  return (a + b - 1n) / b;
};

const totalBps = (f: FeeParams) => f.tradeBps + f.creatorBps + (f.poolBps ?? 0n);

function feesOn(amount: bigint, f: FeeParams) {
  const fee = (amount * f.tradeBps) / BPS;
  const creatorFee = (amount * f.creatorBps) / BPS;
  const poolFee = (amount * (f.poolBps ?? 0n)) / BPS;
  return { fee, creatorFee, poolFee, net: amount - fee - creatorFee - poolFee };
}

export function quoteBuy(pool: Pool, zatIn: bigint, f: FeeParams): BuyQuote {
  if (zatIn <= 0n) throw new Error('zatIn must be positive');
  if (totalBps(f) >= BPS) throw new Error('fees too high');
  const k = pool.vZat * pool.vTok;
  let { fee, creatorFee, poolFee, net } = feesOn(zatIn, f);
  let zatUsed = zatIn;
  let tokensOut = pool.vTok - ceilDiv(k, pool.vZat + net);
  let graduates = false;

  if (pool.mode === 'curve' && tokensOut >= pool.realTok) {
    // Buyer would take the rest of the curve: fill exactly what is left
    // and refund the unused input.
    tokensOut = pool.realTok;
    const netNeeded = ceilDiv(k, pool.vTok - tokensOut) - pool.vZat;
    zatUsed = grossFor(netNeeded, f);
    if (zatUsed > zatIn) zatUsed = zatIn; // rounding guard; net still >= netNeeded below
    ({ fee, creatorFee, poolFee, net } = feesOn(zatUsed, f));
    if (net < netNeeded) throw new Error('internal: cannot fill curve remainder');
    graduates = true;
  }
  if (tokensOut <= 0n) throw new Error('amount too small');
  return { zatUsed, refund: zatIn - zatUsed, fee, creatorFee, poolFee, net, tokensOut, graduates };
}

/** Smallest gross amount whose net (after fees) is at least `net`. */
function grossFor(net: bigint, f: FeeParams): bigint {
  let gross = ceilDiv(net * BPS, BPS - totalBps(f));
  // Fees round down per component, so this is at most a couple of steps.
  while (feesOn(gross, f).net < net) gross += 1n;
  return gross;
}

/**
 * Gross ZEC a buyer must pay to receive at least `tokens` from this pool, or
 * null if the pool cannot sell that many (the rest of the curve is smaller).
 */
export function costForTokens(pool: Pool, tokens: bigint, f: FeeParams): bigint | null {
  if (tokens <= 0n) return 0n;
  if (tokens >= pool.realTok || tokens >= pool.vTok) return null;
  const k = pool.vZat * pool.vTok;
  return grossFor(ceilDiv(k, pool.vTok - tokens) - pool.vZat, f);
}

/**
 * The largest gross ZEC whose buy from this pool yields at most `tokens`, or
 * null if even buying out the rest of the curve stays within `tokens`.
 * Exact (binary search over quoteBuy), so a cap built on it is never passed.
 */
export function maxBuyWithin(pool: Pool, tokens: bigint, f: FeeParams): bigint | null {
  const out = (g: bigint) => {
    if (g <= 0n) return 0n;
    try {
      const q = quoteBuy(pool, g, f);
      return q.graduates ? null : q.tokensOut;
    } catch {
      return 0n; // too small to buy anything
    }
  };
  const hi0 = costForTokens(pool, tokens + 1n, f);
  if (hi0 === null) return null;
  let lo = 0n; // out(lo) <= tokens
  let hi = hi0; // out(hi) > tokens (or graduates)
  while (hi - lo > 1n) {
    const mid = (lo + hi) / 2n;
    const o = out(mid);
    if (o !== null && o <= tokens) lo = mid;
    else hi = mid;
  }
  return lo;
}

export function applyBuy(pool: Pool, q: BuyQuote): Pool {
  // Tokens are priced on `net`; the pool fee lands afterwards, so it lifts the price for holders.
  const next: Pool = {
    ...pool,
    vZat: pool.vZat + q.net + q.poolFee,
    vTok: pool.vTok - q.tokensOut,
    realZat: pool.realZat + q.net + q.poolFee,
    realTok: pool.realTok - q.tokensOut,
  };
  if (next.realTok < 0n) throw new Error('internal: realTok < 0');
  if (next.vZat * next.vTok < pool.vZat * pool.vTok) throw new Error('internal: k decreased');
  if (pool.mode === 'curve' && next.realTok === 0n) return graduate(next);
  return next;
}

function graduate(p: Pool): Pool {
  return {
    mode: 'amm',
    vZat: p.realZat,
    vTok: p.reserveTok,
    realZat: p.realZat,
    realTok: p.reserveTok,
    reserveTok: 0n,
  };
}

export function quoteSell(pool: Pool, tokensIn: bigint, f: FeeParams): SellQuote {
  if (tokensIn <= 0n) throw new Error('tokensIn must be positive');
  const k = pool.vZat * pool.vTok;
  const gross = pool.vZat - ceilDiv(k, pool.vTok + tokensIn);
  if (gross <= 0n) throw new Error('amount too small');
  if (gross > pool.realZat) throw new Error('internal: pool cannot pay');
  if (totalBps(f) >= BPS) throw new Error('fees too high');
  const { fee, creatorFee, poolFee, net } = feesOn(gross, f);
  return { tokensIn, gross, fee, creatorFee, poolFee, zatOut: net };
}

export function applySell(pool: Pool, q: SellQuote): Pool {
  const out = q.gross - q.poolFee; // the pool fee never leaves the pool
  const next: Pool = {
    ...pool,
    vZat: pool.vZat - out,
    vTok: pool.vTok + q.tokensIn,
    realZat: pool.realZat - out,
    realTok: pool.realTok + q.tokensIn,
  };
  if (next.vZat * next.vTok < pool.vZat * pool.vTok) throw new Error('internal: k decreased');
  return next;
}

/** Spot price in zatoshi per 1 whole token, scaled by 1e8 for display precision. */
export function spotPriceZatPerToken(pool: Pool, decimals: number): number {
  const scale = 10n ** BigInt(decimals);
  // zat per whole token = vZat * scale / vTok ; keep 8 extra digits
  const v = (pool.vZat * scale * 100_000_000n) / pool.vTok;
  return Number(v) / 1e8;
}

/** Market cap in zatoshi: spot price × total supply. */
export function marketCapZat(pool: Pool, totalSupply: bigint): bigint {
  return (pool.vZat * totalSupply) / pool.vTok;
}

/** Curve progress in basis points (10000 = graduated). */
export function progressBps(pool: Pool, curveSupply: bigint): number {
  if (pool.mode === 'amm') return 10_000;
  return Number(((curveSupply - pool.realTok) * BPS) / curveSupply);
}
